import type { BadgeTone } from "@/components/ui/Badge";
import { AP_INVOICE_DRAFT_VENDOR_NUMBER, type ApInvoiceRow } from "./apInvoicesApi";

export function formatApInvoiceTshAmountLabel(amount: string | number | null | undefined): string {
  if (amount == null || amount === "") return "";
  const parsed = typeof amount === "number" ? amount : Number.parseFloat(String(amount));
  if (!Number.isFinite(parsed) || parsed <= 0) return "";
  return parsed.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Plain amount for Megaventory line entry: digits and a decimal point only. */
export function apInvoiceTshAmountClipboardText(amount: string | number | null | undefined): string {
  if (amount == null || amount === "") return "";
  const normalized = String(amount).replace(/[$,\s]/g, "");
  const parsed = Number.parseFloat(normalized);
  if (!Number.isFinite(parsed) || parsed <= 0) return "";
  return parsed.toFixed(2);
}

export const TSH_SECTION_HOVER_HINT =
  "Tariff, shipping, and handling (TSH) charges from the vendor invoice. These amounts are not purchase order stock lines and are not sent to the GIPI. After submit, a human creates a separate Megaventory TSH document (parented to the GIPI) with service stock keeping unit lines. Digitized invoice total may include these amounts so it can match the PDF.";

export const TSH_TARIFF_HOVER_HINT =
  "Tariff or import tax on the vendor invoice. When you create the Megaventory TSH, add a line with the tariff service stock keeping unit and this amount.";

export const TSH_SHIPPING_HOVER_HINT =
  "Shipping or freight on the vendor invoice. When you create the Megaventory TSH, add a line with the shipping-d (domestic) or shipping-i (international) service stock keeping unit and this amount.";

export const TSH_ONE_OFF_HOVER_HINT =
  "Miscellaneous or one-off fee that is not shipping or tariff. When you create the Megaventory TSH, add a line with the fee service stock keeping unit and this amount.";

export const TSH_ONE_OFF_DESCRIPTION_HOVER_HINT =
  "Optional label for the Megaventory fee line when the vendor charge is not obvious from the amount alone (for example restocking fee). Leave blank for shipping and tariff; those use fixed service stock keeping units.";

/** @deprecated Use field-specific TSH hover hints. */
export const TSH_CHARGE_HOVER_HINT = TSH_SECTION_HOVER_HINT;

export function apInvoiceTshWorkflowBadgeTone(
  workflowStatus: string | null | undefined,
): BadgeTone {
  switch ((workflowStatus ?? "").trim().toLowerCase()) {
    case "waiting_for_gipi":
      return "warning";
    case "ready_for_human":
      return "info";
    case "awaiting_ingest":
      return "warning";
    case "needs_link_review":
      return "warning";
    case "linked":
      return "success";
    case "failed":
      return "danger";
    default:
      return "neutral";
  }
}

export function apInvoiceShowsTshWorkflow(row: ApInvoiceRow): boolean {
  return row.status === "pending_tsh" || parseApInvoiceTshAmount(row.tsh_amount) != null;
}

/** False while a mixed stock+TSH invoice is still waiting for hub GIPI matching. */
export function canCompleteApInvoiceTshWorkflow(row: ApInvoiceRow): boolean {
  if (row.status !== "pending_tsh") {
    return false;
  }
  const workflow = row.tsh_workflow;
  if (workflow != null) {
    if (workflow.can_open_megaventory || workflow.can_choose_parent) {
      return true;
    }
    const status = (workflow.status ?? row.tsh_workflow_status ?? "").trim().toLowerCase();
    return (
      status === "awaiting_ingest" || status === "needs_link_review" || status === "failed"
    );
  }
  const status = (row.tsh_workflow_status ?? "").trim().toLowerCase();
  return status !== "" && status !== "waiting_for_gipi";
}

/** Vendor-printed invoice number used as Megaventory TSH Document References application reference. */
export function apInvoiceMegaventoryTshReference(
  vendorInvoiceNumber: string | null | undefined,
): string {
  const trimmed = (vendorInvoiceNumber ?? "").trim();
  if (trimmed === "" || trimmed === AP_INVOICE_DRAFT_VENDOR_NUMBER) {
    return "";
  }
  return trimmed;
}

export function parseApInvoiceTshAmount(amount: string | null | undefined): number | null {
  if (amount == null || amount.trim() === "") return null;
  const parsed = Number.parseFloat(amount);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

/** Megaventory mv_number already includes the TSH prefix (for example "TSH 2553"). */
export function formatApInvoiceLinkedTshMvNumber(mvNumber: string | null | undefined): string {
  const trimmed = (mvNumber ?? "").trim();
  if (trimmed === "") {
    return "";
  }
  return /^tsh\b/i.test(trimmed) ? trimmed : `TSH ${trimmed}`;
}

/** Megaventory mv_number often already includes the GIPI prefix (for example "GIPI 3582"). */
export function formatApInvoiceLinkedGipiMvNumber(mvNumber: string | null | undefined): string {
  const trimmed = (mvNumber ?? "").trim();
  if (trimmed === "") {
    return "";
  }
  return /^gipi\b/i.test(trimmed) ? trimmed : `GIPI ${trimmed}`;
}

export function formatApInvoiceTshParentCandidateLabel(candidate: {
  mv_number: string | null;
  supplier_name?: string | null;
  purchase_order_number?: string | null;
  invoice_date: string | null;
  total_amount: string | null;
  currency: string | null;
}): string {
  const parts: string[] = [];
  if (candidate.mv_number != null && candidate.mv_number.trim() !== "") {
    parts.push(candidate.mv_number.trim());
  }
  const supplierName = candidate.supplier_name?.trim() ?? "";
  if (supplierName !== "") {
    parts.push(supplierName);
  }
  const purchaseOrderNumber = candidate.purchase_order_number?.trim() ?? "";
  if (purchaseOrderNumber !== "") {
    parts.push(`PO ${purchaseOrderNumber}`);
  }
  if (candidate.invoice_date != null && candidate.invoice_date.trim() !== "") {
    parts.push(candidate.invoice_date.trim());
  }
  if (candidate.total_amount != null && candidate.total_amount.trim() !== "") {
    const currency = candidate.currency?.trim() ?? "USD";
    parts.push(`${candidate.total_amount.trim()} ${currency}`);
  }
  return parts.length > 0 ? parts.join(" · ") : "GIPI";
}

/** Standalone TSH uses GIPI/purchase-order search. The draft classification can lead the saved row. */
export function apInvoiceUsesStandaloneGipiSearch(input: {
  standaloneDraft: boolean;
  purchaseKind: string | null | undefined;
}): boolean {
  return input.standaloneDraft || input.purchaseKind === "standalone_tsh";
}

/**
 * Parent search runs as soon as the form is standalone TSH.
 * Waiting for the classification patch leaves the field on Loading.
 */
export function apInvoiceStandaloneGipiSearchReady(input: {
  standaloneDraft: boolean;
  purchaseKind: string | null | undefined;
}): boolean {
  if (input.standaloneDraft || input.purchaseKind === "standalone_tsh") return true;
  return !apInvoiceUsesStandaloneGipiSearch(input);
}
